import React, { forwardRef } from 'react';

import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import ReactDatePicker, { DatePickerProps, registerLocale } from 'react-datepicker';
import en from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr';
import es from 'date-fns/locale/es';
import sv from 'date-fns/locale/sv';
import { DateTime } from 'luxon';

import { useAppSelector } from 'hooks';

type IDatePickerProps = {
  localizeDate?: boolean;
  dataTestId?: string;
} & DatePickerProps;

const POPPER_MODIFIERS = [
  {
    name: 'preventOverflow',
    options: {
      enabled: true,
      boundary: 'viewport',
      altAxis: false
    },
    fn: (state: any) => state
  },
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['top', 'bottom']
    },
    fn: (state: any) => state
  }
];

const DatePicker = ({ localizeDate = true, disabled = false, ...props }: IDatePickerProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);
  registerLocale('en', en);
  registerLocale('fr', fr);
  registerLocale('es', es);
  registerLocale('sv', sv);

  const getDateString = (value: string) => {
    if (props.selected) {
      if (!props.dateFormat) {
        let dateTime = DateTime.fromJSDate(props.selected).setLocale(locale);
        if (!localizeDate) {
          dateTime = dateTime.set({ hour: 12 });
        }
        if (props.showTimeInput) {
          return dateTime.toLocaleString(DateTime.DATETIME_MED);
        } else {
          return dateTime.toLocaleString(DateTime.DATE_MED);
        }
      } else {
        return value;
      }
    }
    return props.placeholderText || intl.formatMessage({ id: 'select' });
  };

  const ButtonInput = forwardRef(({ value, onClick, className }: any, ref) => (
    <Button
      data-test={props.dataTestId}
      data-test-value={getDateString(value)}
      size="sm"
      variant="outline-primary"
      onClick={onClick}
      ref={ref as any}
      className={className}
      disabled={disabled}
    >
      {getDateString(value)}
    </Button>
  ));

  return (
    <ReactDatePicker
      customInput={<ButtonInput />}
      popperPlacement="bottom"
      dayClassName={(date) => `month-${date.getMonth() + 1}-day-${date.getDate()}`}
      placeholderText={intl.formatMessage({ id: 'datePlaceholder' })}
      popperModifiers={POPPER_MODIFIERS}
      locale={locale}
      data-test={props.dataTestId}
      {...props}
    />
  );
};

export default DatePicker;
